import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Microphone permission, and which microphone / speakers a call uses.
 *
 * Kept out of TwilioCallProvider because that file is already large and this is
 * a self-contained concern: everything here talks to `device.audio` (the SDK's
 * AudioHelper) or to `navigator.mediaDevices`, and none of it touches call
 * state.
 *
 * The two halves ship together for a reason. `enumerateDevices` returns entries
 * with EMPTY labels until microphone permission is granted, so "Microphone 1 /
 * Microphone 2" is the best a picker can do until the user has approved access.
 * The permission step is what makes the picker usable.
 */

export const MIC_PERMISSION = {
  UNKNOWN: "unknown",
  GRANTED: "granted",
  PROMPT: "prompt",
  DENIED: "denied",
  UNSUPPORTED: "unsupported",
};

const AUDIO_PREFS_STORAGE_KEY = "twilioAudioDevicePrefs";

const EMPTY_PREFS = {
  inputDeviceId: "",
  outputDeviceId: "",
  ringtoneDeviceId: "",
};

const readAudioPrefs = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem(AUDIO_PREFS_STORAGE_KEY));

    return {
      inputDeviceId: parsed?.inputDeviceId || "",
      outputDeviceId: parsed?.outputDeviceId || "",
      ringtoneDeviceId: parsed?.ringtoneDeviceId || "",
    };
  } catch {
    return { ...EMPTY_PREFS };
  }
};

const writeAudioPrefs = (prefs) => {
  try {
    localStorage.setItem(AUDIO_PREFS_STORAGE_KEY, JSON.stringify(prefs));
  } catch (error) {
    console.warn("Could not save the audio device choice:", error);
  }
};

/**
 * AudioHelper exposes availableInputDevices / availableOutputDevices as Maps of
 * deviceId to MediaDeviceInfo. Flatten to something a picker can render, and
 * cover the no-label case rather than showing a list of blanks.
 */
const toDeviceList = (deviceMap, fallbackPrefix) => {
  if (typeof deviceMap?.forEach !== "function") return [];

  const list = [];

  deviceMap.forEach((info, deviceId) => {
    list.push({
      deviceId,
      label: info?.label || `${fallbackPrefix} ${list.length + 1}`,
    });
  });

  return list;
};

export const useTwilioAudioDevices = () => {
  const deviceRef = useRef(null);
  const prefsRef = useRef(readAudioPrefs());
  const hasInputStreamRef = useRef(false);
  const permissionStatusRef = useRef(null);

  const [micPermission, setMicPermission] = useState(MIC_PERMISSION.UNKNOWN);
  const [micPermissionError, setMicPermissionError] = useState("");
  const [audioInputDevices, setAudioInputDevices] = useState([]);
  const [audioOutputDevices, setAudioOutputDevices] = useState([]);
  const [audioPrefs, setAudioPrefs] = useState(prefsRef.current);
  const [isOutputSelectionSupported, setIsOutputSelectionSupported] =
    useState(false);
  const [isVolumeSupported, setIsVolumeSupported] = useState(false);
  const [inputVolume, setInputVolume] = useState(0);

  const refreshDevices = useCallback(() => {
    const audio = deviceRef.current?.audio;

    if (!audio) return;

    setAudioInputDevices(
      toDeviceList(audio.availableInputDevices, "Microphone")
    );
    setAudioOutputDevices(toDeviceList(audio.availableOutputDevices, "Speaker"));
    setIsOutputSelectionSupported(!!audio.isOutputSelectionSupported);
    setIsVolumeSupported(!!audio.isVolumeSupported);
  }, []);

  /**
   * Speaker and ringtone are set on the AudioHelper and persist across calls, so
   * they can be applied the moment the device exists. They need no media stream.
   *
   * Keeping them separate is the point: call audio can go to a headset while the
   * incoming ringtone stays on the laptop speakers, so a call is still heard
   * when the headset is not being worn.
   */
  const applyOutputPreferences = useCallback(() => {
    const audio = deviceRef.current?.audio;

    if (!audio?.isOutputSelectionSupported) return;

    const { outputDeviceId, ringtoneDeviceId } = prefsRef.current;

    const applyTo = (collection, deviceId) => {
      if (!deviceId || !audio.availableOutputDevices?.has?.(deviceId)) return;

      try {
        collection.set(deviceId);
      } catch (error) {
        console.warn("Could not select that output device:", error);
      }
    };

    applyTo(audio.speakerDevices, outputDeviceId);
    applyTo(audio.ringtoneDevices, ringtoneDeviceId);
  }, []);

  /**
   * setInputDevice ACQUIRES the microphone and holds it. That is what makes the
   * level meter work, but it also lights the browser's recording indicator, so
   * it is only done when a call needs it or the settings panel is open, and
   * released again afterwards.
   */
  const applyInputPreference = useCallback(async () => {
    const audio = deviceRef.current?.audio;
    const { inputDeviceId } = prefsRef.current;

    if (!audio || !inputDeviceId) return;
    if (!audio.availableInputDevices?.has?.(inputDeviceId)) return;

    try {
      await audio.setInputDevice(inputDeviceId);
      hasInputStreamRef.current = true;
    } catch (error) {
      console.warn("Could not select that microphone:", error);
    }
  }, []);

  const releaseInputDevice = useCallback(async () => {
    const audio = deviceRef.current?.audio;

    if (!audio || !hasInputStreamRef.current) return;

    try {
      await audio.unsetInputDevice();
      hasInputStreamRef.current = false;
      setInputVolume(0);
    } catch (error) {
      console.warn("Could not release the microphone:", error);
    }
  }, []);

  /**
   * permissions.query reads the current state WITHOUT prompting, which is what
   * lets the panel show the right thing before the user does anything. Firefox
   * does not support the "microphone" name, so fall back to inferring from
   * device labels, which stay empty until access has been granted.
   */
  const refreshMicPermission = useCallback(async () => {
    try {
      const status = await navigator.permissions.query({ name: "microphone" });

      permissionStatusRef.current = status;
      setMicPermission(status.state);

      status.onchange = () => setMicPermission(status.state);

      return status.state;
    } catch {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const hasLabelledInput = devices.some(
          (device) => device.kind === "audioinput" && device.label
        );
        const state = hasLabelledInput
          ? MIC_PERMISSION.GRANTED
          : MIC_PERMISSION.UNSUPPORTED;

        setMicPermission(state);

        return state;
      } catch {
        setMicPermission(MIC_PERMISSION.UNSUPPORTED);

        return MIC_PERMISSION.UNSUPPORTED;
      }
    }
  }, []);

  const requestMicPermission = useCallback(async () => {
    setMicPermissionError("");

    if (!navigator.mediaDevices?.getUserMedia) {
      setMicPermission(MIC_PERMISSION.UNSUPPORTED);
      setMicPermissionError("This browser cannot reach a microphone.");

      return false;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      // Release it immediately. This call exists to raise the prompt and to
      // unlock device labels, not to hold the microphone open.
      stream.getTracks().forEach((track) => track.stop());

      setMicPermission(MIC_PERMISSION.GRANTED);
      refreshDevices();

      return true;
    } catch (error) {
      console.error("Microphone permission request failed:", error);

      if (error?.name === "NotAllowedError" || error?.name === "SecurityError") {
        setMicPermission(MIC_PERMISSION.DENIED);
        setMicPermissionError(
          "Microphone access is blocked. Allow it in your browser's site settings for this page, then reload."
        );
      } else if (
        error?.name === "NotFoundError" ||
        error?.name === "DevicesNotFoundError"
      ) {
        setMicPermissionError("No microphone was found on this computer.");
      } else {
        setMicPermissionError(
          error?.message || "Could not reach the microphone."
        );
      }

      return false;
    }
  }, [refreshDevices]);

  const savePrefs = useCallback((patch) => {
    const next = { ...prefsRef.current, ...patch };

    prefsRef.current = next;
    setAudioPrefs(next);
    writeAudioPrefs(next);

    return next;
  }, []);

  const selectInputDevice = useCallback(
    async (deviceId) => {
      savePrefs({ inputDeviceId: deviceId || "" });

      if (!deviceId) {
        await releaseInputDevice();
        return;
      }

      await applyInputPreference();
    },
    [applyInputPreference, releaseInputDevice, savePrefs]
  );

  const selectOutputDevice = useCallback(
    (deviceId) => {
      savePrefs({ outputDeviceId: deviceId || "" });
      applyOutputPreferences();
    },
    [applyOutputPreferences, savePrefs]
  );

  const selectRingtoneDevice = useCallback(
    (deviceId) => {
      savePrefs({ ringtoneDeviceId: deviceId || "" });
      applyOutputPreferences();
    },
    [applyOutputPreferences, savePrefs]
  );

  const testSpeakerDevice = useCallback(async () => {
    const audio = deviceRef.current?.audio;

    if (!audio?.isOutputSelectionSupported) return;

    try {
      await audio.speakerDevices.test();
    } catch (error) {
      console.warn("Could not play the test tone:", error);
    }
  }, []);

  /**
   * Called when the audio settings panel opens. Grabs the microphone so the
   * level meter has something to read, and makes sure the lists are current.
   */
  const startAudioPreview = useCallback(async () => {
    await refreshMicPermission();
    refreshDevices();
    await applyInputPreference();
  }, [applyInputPreference, refreshDevices, refreshMicPermission]);

  const stopAudioPreview = useCallback(
    async ({ keepStream = false } = {}) => {
      if (keepStream) return;

      await releaseInputDevice();
    },
    [releaseInputDevice]
  );

  /**
   * Handed the Device as soon as it exists. The AudioHelper is created with the
   * Device, so this is the earliest point the lists and the saved output
   * choices can be applied.
   */
  const bindDevice = useCallback(
    (device) => {
      deviceRef.current = device;

      if (!device?.audio) return;

      refreshDevices();
      applyOutputPreferences();

      device.audio.on("deviceChange", (lostActiveDevices) => {
        // Fires when a headset is plugged in or pulled out. Re-read the lists,
        // then re-apply, so a device coming back is picked up again.
        refreshDevices();
        applyOutputPreferences();

        if (lostActiveDevices?.length) {
          console.warn(
            "Audio device removed while selected:",
            lostActiveDevices.map((lost) => lost?.label || lost?.deviceId)
          );
        }
      });

      if (device.audio.isVolumeSupported) {
        device.audio.on("inputVolume", (volume) => setInputVolume(volume));
      }
    },
    [applyOutputPreferences, refreshDevices]
  );

  useEffect(() => {
    refreshMicPermission();

    return () => {
      const status = permissionStatusRef.current;

      if (status) status.onchange = null;
    };
  }, [refreshMicPermission]);

  return {
    micPermission,
    micPermissionError,
    isMicBlocked: micPermission === MIC_PERMISSION.DENIED,
    audioInputDevices,
    audioOutputDevices,
    audioPrefs,
    isOutputSelectionSupported,
    isVolumeSupported,
    inputVolume,
    requestMicPermission,
    refreshMicPermission,
    selectInputDevice,
    selectOutputDevice,
    selectRingtoneDevice,
    testSpeakerDevice,
    startAudioPreview,
    stopAudioPreview,
    applyInputPreference,
    releaseInputDevice,
    bindDevice,
  };
};

export default useTwilioAudioDevices;
